import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

/**
 * The planning-evidence panel's own copy — F-2.
 *
 * Why this module exists at all: these six strings used to be hardcoded
 * literals inside `planning-evidence.ts`, a module that took no locale
 * parameter. Copy that lives outside a locale-keyed home can neither be
 * translated nor asserted against — and that is not a coincidence, it is the
 * same property twice. Two independently-written browser specs each assert
 * verbatim contents on every restored composer surface EXCEPT this panel and
 * its sibling, where both degrade to `not.toBe("")`; the split in the tests
 * followed the split in the code.
 *
 * Giving the strings an authored home is therefore the fix, not a step towards
 * it: the localization arrives carrying its own assertion target
 * (`PLANNING_EVIDENCE_COPY[locale].planPrepared`), which is what those two
 * specs can now assert instead of `not.toBe("")`.
 *
 * Priority note: the panel was invisible when the sweep measured it
 * (`getClientRects().length === 0` on 100% of samples). `ab2136640` restored
 * it — 606×48 on the primary creation flow — so its audience went from nobody
 * to everybody without a single string changing.
 */
export interface PlanningEvidenceCopy {
  /** Title once the live planner has returned a plan. */
  planPrepared: string
  /** Detail: the planner read the brief before the workspace opened. */
  checkedBrief: string
  /** Detail: a plan was produced, without the provider/model receipt. */
  preparedFromBrief: string
  /** Title while the planner is still working. */
  checkingTheBrief: string
  /** Title when the local fallback planner produced the plan. */
  needsCloserReview: string
  /** Title when the live planner timed out. */
  takingLonger: string
  /** Detail for the fallback case, when the producer gave no reason. */
  localPlanReview: string
}

export const PLANNING_EVIDENCE_COPY: Record<LocaleCode, PlanningEvidenceCopy> = {
  en: {
    planPrepared: 'Plan prepared',
    checkedBrief: 'Coogo checked the brief before opening the workspace.',
    preparedFromBrief: 'Coogo prepared the plan from your brief.',
    checkingTheBrief: 'Checking the brief',
    needsCloserReview: 'Needs a closer review',
    takingLonger: 'Taking longer than expected',
    localPlanReview: 'Coogo prepared a local plan. Review it before sharing.',
  },
  ko: {
    planPrepared: '계획을 준비했습니다',
    checkedBrief: '작업 공간을 열기 전에 요청 내용을 확인했습니다.',
    preparedFromBrief: '요청 내용을 바탕으로 계획을 준비했습니다.',
    checkingTheBrief: '요청 내용을 확인하고 있습니다',
    needsCloserReview: '조금 더 확인이 필요합니다',
    takingLonger: '예상보다 오래 걸리고 있습니다',
    localPlanReview: '로컬에서 계획을 준비했습니다. 공유하기 전에 확인해 주세요.',
  },
  ja: {
    planPrepared: '計画を準備しました',
    checkedBrief: 'ワークスペースを開く前にご依頼内容を確認しました。',
    preparedFromBrief: 'ご依頼内容から計画を準備しました。',
    checkingTheBrief: 'ご依頼内容を確認しています',
    needsCloserReview: 'もう少し確認が必要です',
    takingLonger: '予想より時間がかかっています',
    localPlanReview:
      'ローカルで計画を準備しました。共有する前に確認してください。',
  },
  zh: {
    planPrepared: '已准备好规划',
    checkedBrief: '打开工作区之前已经核对过你的需求。',
    preparedFromBrief: '已根据你的需求准备好规划。',
    checkingTheBrief: '正在核对需求',
    needsCloserReview: '需要再仔细看看',
    takingLonger: '花的时间比预期长',
    localPlanReview: '已在本地准备好规划。分享前请先检查。',
  },
  'zh-TW': {
    planPrepared: '已準備好規劃',
    checkedBrief: '開啟工作區之前已經核對過你的需求。',
    preparedFromBrief: '已依照你的需求準備好規劃。',
    checkingTheBrief: '正在核對需求',
    needsCloserReview: '需要再仔細看看',
    takingLonger: '花的時間比預期長',
    localPlanReview: '已在本機準備好規劃。分享前請先檢查。',
  },
}
